
import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';

interface PropertyHighlight {
  title: string;
  description: string;
}

interface PropertyHighlightsFormProps {
  highlights: PropertyHighlight[];
  onHighlightsChange: (highlights: PropertyHighlight[]) => void;
}

const PropertyHighlightsForm = ({ highlights, onHighlightsChange }: PropertyHighlightsFormProps) => {
  const addHighlight = () => {
    if (highlights.length < 3) {
      onHighlightsChange([...highlights, { title: '', description: '' }]);
    }
  };

  const removeHighlight = (index: number) => {
    const newHighlights = highlights.filter((_, i) => i !== index);
    onHighlightsChange(newHighlights);
  };

  const updateHighlight = (index: number, field: 'title' | 'description', value: string) => {
    const newHighlights = highlights.map((highlight, i) => 
      i === index ? { ...highlight, [field]: value } : highlight
    );
    onHighlightsChange(newHighlights);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">Características Destacadas</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addHighlight}
          disabled={highlights.length >= 3}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Agregar Característica
        </Button>
      </div>
      
      <p className="text-sm text-gray-600">
        Agrega hasta 3 características que destaquen de tu propiedad
      </p>

      {highlights.length === 0 && (
        <div className="text-center p-6 border-2 border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-500">No hay características agregadas</p>
          <Button
            type="button"
            variant="outline"
            onClick={addHighlight}
            className="mt-2"
          >
            Agregar Primera Característica
          </Button>
        </div>
      )}

      {highlights.map((highlight, index) => (
        <div key={index} className="border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Característica {index + 1}</h4>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeHighlight(index)}
              className="text-red-500 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="space-y-3">
            <div>
              <Label htmlFor={`highlight-title-${index}`}>Título *</Label>
              <Input
                id={`highlight-title-${index}`}
                value={highlight.title}
                onChange={(e) => updateHighlight(index, 'title', e.target.value)}
                placeholder="Ej: Ubicación céntrica"
                maxLength={50}
              />
            </div>
            
            <div>
              <Label htmlFor={`highlight-description-${index}`}>Descripción *</Label>
              <Textarea
                id={`highlight-description-${index}`}
                value={highlight.description}
                onChange={(e) => updateHighlight(index, 'description', e.target.value)}
                placeholder="Ej: A solo 5 minutos del centro histórico"
                rows={2}
                maxLength={150}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PropertyHighlightsForm;
